import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

import type { Product } from '@/Data/products';
import { fetchProducts } from '@/services/ProductService';

import AOS from 'aos';
import 'aos/dist/aos.css';

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ================================
  // AOS Init (run once only)
  // ================================
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  // ================================
  // Load featured products
  // ================================
  useEffect(() => {
    const load = async () => {
      try {
        const all = await fetchProducts();
        const featured = all.filter((p) => p.isFeatured);
        setProducts(featured);
      } catch {
        setError('Failed to load featured products.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // No AOS.refresh — prevents flicker + content reappear

  // ================================
  // Render states
  // ================================
  if (loading) {
    return (
      <section className="py-12 text-center">
        Loading featured products...
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 text-center text-red-500">{error}</section>
    );
  }

  return (
    <section
      data-aos="fade-up"
      className="bg-[#FAFAFA] px-4 py-12 text-center sm:px-8 md:px-12 lg:px-20 xl:px-36"
    >
      {/* === Section Title === */}
      <h2 className="text-2xl font-extrabold tracking-tighter text-[#0B0B0B] sm:text-3xl lg:text-4xl">
        Featured Products
      </h2>

      <p className="mx-auto mt-3 max-w-lg py-4 text-xs font-medium tracking-tight text-gray-700 sm:text-sm">
        <span className="font-bold text-[#0B0B0B]">Our Armored Solution</span> –
        Engineered for protection, tested for performance. Explore our current
        and upcoming ballistic armor offerings.
      </p>

      {/* === Button === */}
      <div className="mt-6 flex justify-center">
        <Link
          to="/products"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <button className="shadow-xs flex items-center gap-4 rounded-md bg-[#FFBD59] px-5 py-2.5 text-xs font-medium tracking-tighter text-black transition hover:bg-[#e49718] hover:shadow-lg sm:px-6 sm:py-3 sm:text-sm md:px-12 md:py-5 md:text-base">
            <Shield size={23} />
            See All Products
          </button>
        </Link>
      </div>

      {/* === Product Grid === */}
      <div className="mx-auto mt-10 grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
        {products.map((p) => {
          const image =
            p.thumbnails?.[0] ||
            p.mediaGallery?.find((m) => m.fileType === 'image')?.fileUrl ||
            '/fallback/no-image.png';

          return (
            <Link
              to={`/${p.slug}`}
              key={p.id}
              className="group relative mx-auto flex w-full max-w-xs flex-col overflow-hidden rounded-lg bg-white text-left transition hover:scale-[1.02] sm:max-w-sm"
              data-aos="fade-up"
            >
              {/* === Image === */}
              <div className="relative h-64 w-full overflow-hidden sm:h-72 md:h-64 2xl:h-72">
                <img
                  src={image}
                  alt={p.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 transition group-hover:bg-black/10" />
              </div>

              {/* === Info === */}
              <div className="flex flex-1 flex-col justify-between px-4 py-3 sm:px-5 sm:py-4 md:px-1">
                <div>
                  <h3 className="lg:text-md text-sm font-extrabold tracking-tighter text-[#0B0B0B] sm:text-base">
                    {p.name}
                  </h3>

                  <p className="mt-1 text-xs font-medium tracking-tight text-black sm:text-xs md:text-[10px] 2xl:text-xs">
                    {p.description}
                  </p>
                </div>

                <p className="mt-3 pt-10 text-base font-extrabold text-[#0B0B0B] sm:text-lg lg:text-xl">
                  ₱{' '}
                  {p.price.toLocaleString('en-PH', {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
